// Offline magazyn plików (Blob) w IndexedDB.
// Używamy tego dla uploadu /files, bo localStorage + kolejka nie nadają się na binarkę.
//
// UWAGA: Celowo bez zewnętrznych zależności (idb-keyval), żeby nie blokować prac na instalacji paczek.
// Prosty wrapper na IndexedDB wystarcza na nasze potrzeby.

export type OfflineFileBlobRecord = {
  id: string; // foreign key (np. offlineOperationId)
  name: string;
  type: string; // mime
  blob: Blob;
  size: number;
  lastModified?: number;
};

// Klucze w IndexedDB:
// - dla uploadu OFFLINE używamy op.tempId (uuid operacji)
// - dla plików pobranych podczas synchronizacji (z backendu) używamy stabilnego klucza po uuid pliku
export const makeServerFileBlobId = (fileUuid: string) => `server-file:${fileUuid}`;

const DB_NAME = 'sommartdom-offline';
const STORE_NAME = 'fileBlobs';
const DB_VERSION = 1;

const openDb = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

const withStore = async <T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T>
): Promise<T> => {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, mode);
    const store = tx.objectStore(STORE_NAME);
    const req = fn(store);

    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
};

export const setOfflineFileBlob = async (record: OfflineFileBlobRecord) => {
  await withStore('readwrite', (store) => store.put(record));
};

export const getOfflineFileBlob = async (
  id: string
): Promise<OfflineFileBlobRecord | null> => {
  const rec = await withStore<OfflineFileBlobRecord | undefined>('readonly', (store) =>
    store.get(id)
  );
  return (rec as any) || null;
};

export const setServerFileBlob = async (input: {
  uuid: string;
  name: string;
  type: string;
  blob: Blob;
  size: number;
  lastModified?: number;
}) => {
  await setOfflineFileBlob({
    id: makeServerFileBlobId(input.uuid),
    name: input.name,
    type: input.type,
    blob: input.blob,
    size: input.size,
    lastModified: input.lastModified,
  });
};

export const getServerFileBlob = async (uuid: string) => {
  return getOfflineFileBlob(makeServerFileBlobId(uuid));
};

export const deleteServerFileBlob = async (uuid: string) => {
  return deleteOfflineFileBlob(makeServerFileBlobId(uuid));
};

export const deleteOfflineFileBlob = async (id: string) => {
  await withStore('readwrite', (store) => store.delete(id));
};
